import React, { useEffect, useRef, useState } from "react";
import { css, styled } from "styled-components";
import Button from "./Button";
import { X } from "react-feather";

interface Props {
  children: React.ReactNode;
  buttons?: React.ReactNode;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  subTitle?: React.ReactNode;
  color?: string;
  pad?: boolean;
  modal?: boolean;
  onClose?: () => void;
  width?: string;
  className?: string;
  noMargin?: boolean;
}

/** Card treatment (border/radius/shadow) matches the polish introduced for the Spotter customer-
 *  portal tool's own cards (\`MappingScreen\`'s \`Card\`/\`Row\`) — a visible-but-subtle border in the
 *  same token family as an input's own border (\`--inputBorder\`/\`--dark300\`), a softer two-layer
 *  shadow instead of one hard-edged drop shadow, and a touch more corner radius. Applied here in
 *  the shared component so every \`Section\` everywhere (manager, Vault, customer portal) picks it
 *  up, not just Spotter's own hand-rolled cards. */
const StyledSection = styled.div<{ $modal?: boolean; $noMargin?: boolean }>`
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.22), 0 3px 8px rgba(0, 0, 0, 0.22);
  height: fit-content;
  margin-bottom: ${(props) => (props.$noMargin ? "0" : "15px")};
  background-color: var(--dark100);
  border: 1px solid var(--dark300);
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;

  ${(props) =>
    props.$modal &&
    css`
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
      z-index: 50000;
      border-radius: 15px;
      border: 1px solid var(--dark500);
    `};
`;

/** Text treatment matches Spotter's own \`CardTitle\` (\`MappingScreen\`/\`GlossaryScreen\`/
 *  \`SpotterChangesPanel\`'s \`Title\`) exactly — same font-size/weight/letter-spacing/text-transform/
 *  color/font-family, so every \`Section\` reads as the same "card header" as Spotter's. Scoped to
 *  the title's own \`> span\` rather than the whole bar, so \`ButtonWrapper\`'s buttons (which share
 *  this container but aren't part of Spotter's title) don't inherit the monospace/uppercase/
 *  tracking treatment meant only for the label text. Layout properties (padding/min-height) stay
 *  as-is since this title also hosts inline buttons Spotter's doesn't. */
const StyledTitle = styled.div<{ $modal?: boolean }>`
  background-color: var(--dark100);
  border-bottom: 1px solid var(--dark300);
  min-height: 45px;
  padding: 5px 12px 3px;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  > span {
    display: flex;
    align-items: center;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-size: 13px;
    font-family: ui-monospace, Menlo, monospace;
    color: var(--gray400);

    > * + * {
      margin-left: 10px;
    }
  }

  ${(props) =>
    props.$modal &&
    css`
      padding: 0 15px;
      height: 50px;
    `};
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 5px;
  padding-left: 10px;
  row-gap: 2px;
  justify-content: flex-end;
  flex: 1 1 auto;
`;

const Padding = styled.div<{
  $doPad?: boolean;
  $padBottom?: boolean;
  $modal?: boolean;
}>`
  padding: ${(props) => (props.$doPad ? "20px 10px" : "0")};
  padding-bottom: ${(props) => (props.$padBottom ? "20px" : "0")};
  display: flex;
  min-height: 0; /* allow scrolls to fill height */
  flex-grow: 1;
  flex-direction: column;

  ${(props) =>
    props.$modal &&
    css`
      overflow: auto;
    `};
`;

const Footer = styled.div`
  background-color: var(--dark100);
  padding: 5px 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid var(--dark300);

  > * + * {
    margin-left: 10px;
  }
`;

const ModalBackground = styled.div`
  background-color: var(--overlay);
  position: fixed;
  height: 100vh;
  width: 100vw;
  left: 0;
  top: 0;
  z-index: 50000;
  padding-top: 5vh;
  display: flex;
  justify-content: center;
`;

const Section = (props: Props) => {
  const {
    children,
    title,
    buttons,
    pad,
    footer,
    modal,
    onClose,
    width,
    subTitle,
    className,
    noMargin,
  } = props;
  const backgroundRef = useRef<HTMLDivElement>(null);
  const [mousedownTarget, setMousedownTarget] = useState<EventTarget>();

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (modal && event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", keydown);

    return () => {
      window.removeEventListener("keydown", keydown);
    };
  });

  const section = (
    <StyledSection
      $modal={modal}
      style={
        modal
          ? { width: width ?? "700px", minWidth: width ?? "700px" }
          : undefined
      }
      className={className}
      $noMargin={noMargin}
    >
      {title !== undefined && (
        <StyledTitle $modal={modal}>
          <span>{title}</span>
          <ButtonWrapper>
            {buttons}
            {onClose && <Button icon={X} onClick={() => onClose()} />}
          </ButtonWrapper>
        </StyledTitle>
      )}
      {subTitle}
      <Padding $doPad={pad} $padBottom={!footer && pad} $modal={modal}>
        {children}
      </Padding>
      {footer && <Footer>{footer}</Footer>}
    </StyledSection>
  );

  return (
    <>
      {modal ? (
        <ModalBackground
          ref={backgroundRef}
          onMouseDown={(event) => {
            setMousedownTarget(event.target);
          }}
          onMouseUp={(event) => {
            if (
              event.target === backgroundRef.current &&
              event.target === mousedownTarget
            ) {
              onClose?.();
            }
          }}
        >
          {section}
        </ModalBackground>
      ) : (
        section
      )}
    </>
  );
};

export default Section;
